// LoginPage.jsx - Page d'authentification
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Eye, EyeOff, Building2, Globe, Shield, 
  Activity, CheckCircle2, Clock, Brain, BarChart3, 
  AlertCircle, ArrowRight
} from 'lucide-react';

export default function LoginPage({ onLogin }) {
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${API_URL}/login`, {
        email: email,
        password: password,
      });

      if (res.data.success) {
        const userData = res.data.user;
        localStorage.setItem('user', JSON.stringify(userData));
        onLogin?.(userData);

        if (res.data.premiereConnexion === true) {
          navigate('/change-password');
        } else {
          navigate('/menu');
        }
      } else {
        setError(res.data.message || 'Email ou mot de passe incorrect');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      
      {/* PANNEAU GAUCHE */}
      <div style={styles.left}>
        <div style={styles.leftContent}>
          <div style={styles.brand}>
            <span style={styles.brandIcon}>🌿</span>
            <span style={styles.brandName}>GestCongés</span>
          </div>
          
          <h1 style={styles.headline}>
            La plateforme RH intelligente
            <br />
            <span style={styles.headlineHighlight}>congés, absences et conformité</span>
          </h1>
          
          <p style={styles.description}>
            Du calcul automatique des droits aux validations hiérarchiques, 
            chaque règle RH est appliquée automatiquement pour garantir 
            fiabilité, traçabilité et conformité.
          </p>

          <div style={styles.stats}>
            <div style={styles.statCard}>
              <Activity size={16} style={styles.statIcon} />
              <div>
                <div style={styles.statValue}>45</div>
                <div style={styles.statLabel}>Demandes</div>
              </div>
            </div>
            <div style={styles.statCard}>
              <CheckCircle2 size={16} style={{...styles.statIcon, color: '#a3e635'}} />
              <div>
                <div style={styles.statValue}>32</div>
                <div style={styles.statLabel}>Approuvées</div>
              </div>
            </div>
            <div style={styles.statCard}>
              <Clock size={16} style={{...styles.statIcon, color: '#fbbf24'}} />
              <div>
                <div style={styles.statValue}>13</div>
                <div style={styles.statLabel}>En attente</div>
              </div>
            </div>
          </div>

          <div style={styles.capabilities}>
            <div style={styles.capability}>
              <Building2 size={15} style={styles.capIcon} />
              <span>Multi-société & multi-holding</span>
            </div>
            <div style={styles.capability}>
              <Globe size={15} style={styles.capIcon} />
              <span>Moteur de règles multi-législations</span>
            </div>
            <div style={styles.capability}>
              <Shield size={15} style={styles.capIcon} />
              <span>Validation hiérarchique configurable</span>
            </div>
          </div>

          <div style={styles.aiSection}>
            <div style={styles.aiLabel}>🤖 Intelligence & Analyse</div>
            <div style={styles.aiFeatures}>
              <div style={styles.aiFeature}>
                <Brain size={16} style={styles.aiFeatureIcon} />
                <div>
                  <div style={styles.aiFeatureTitle}>Copilote IA</div>
                  <div style={styles.aiFeatureDesc}>Analyse prédictive des absences</div>
                </div>
              </div>
              <div style={styles.aiFeature}>
                <BarChart3 size={16} style={styles.aiFeatureIcon} />
                <div>
                  <div style={styles.aiFeatureTitle}>Délais de validation</div>
                  <div style={styles.aiFeatureDesc}>Analyse par service et niveau</div>
                </div>
              </div>
              <div style={styles.aiFeature}>
                <AlertCircle size={16} style={styles.aiFeatureIcon} />
                <div>
                  <div style={styles.aiFeatureTitle}>Taux d'absence</div>
                  <div style={styles.aiFeatureDesc}>Temps réel par département</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PANNEAU DROIT */}
      <div style={styles.right}>
        <div style={styles.card}>
          <div style={styles.avatar}>👥</div>
          <h2 style={styles.cardTitle}>Connectez-vous</h2>
          <p style={styles.cardSubtitle}>
            Accédez à votre espace de gestion
          </p>
          
          <form onSubmit={handleLogin} style={styles.form}>
            <input
              style={styles.input}
              type="email"
              placeholder="Email professionnel"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />
            
            <div style={styles.passwordWrapper}>
              <input
                style={styles.inputPassword}
                type={showPassword ? 'text' : 'password'}
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                style={styles.eyeButton}
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            
            {error && <div style={styles.error}>{error}</div>}
            
            <button 
              style={{
                ...styles.button,
                ...(loading ? styles.buttonDisabled : {})
              }} 
              disabled={loading}
              onMouseOver={(e) => !loading && (e.target.style.backgroundColor = '#059669')}
              onMouseOut={(e) => !loading && (e.target.style.backgroundColor = '#10b981')}
            >
              {loading ? 'Connexion...' : (
                <span style={styles.buttonText}>
                  Se connecter <ArrowRight size={16} style={{verticalAlign: 'middle'}} />
                </span>
              )}
            </button>
          </form>

          <div style={styles.forgotPassword}>
            <button 
              type="button" 
              style={styles.forgotButton}
              onClick={() => navigate('/forgot-password')}
            >
              Mot de passe oublié ?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    display: 'flex',
    height: '100vh',
    fontFamily: 'Inter, system-ui, sans-serif',
    overflow: 'hidden',
  },
  
  // PANNEAU GAUCHE
  left: {
    flex: '1',
    background: 'linear-gradient(160deg, #022c22 0%, #064e3b 30%, #065f46 100%)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '40px 50px 30px',
    overflowY: 'auto',
  },
  leftContent: {
    maxWidth: '520px',
    width: '100%',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '24px',
  },
  brandIcon: {
    fontSize: '22px',
  },
  brandName: {
    fontSize: '13px',
    fontWeight: '700',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    opacity: '0.85',
  },
  headline: {
    fontSize: '28px',
    fontWeight: '700',
    lineHeight: '1.3',
    marginBottom: '12px',
    marginTop: '0',
  },
  headlineHighlight: {
    color: '#6ee7b7',
  },
  description: {
    fontSize: '13px',
    opacity: '0.7',
    lineHeight: '1.6',
    marginBottom: '22px',
    marginTop: '0',
  },
  
  // STATS
  stats: {
    display: 'flex',
    gap: '10px',
    marginBottom: '22px',
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(8px)',
    padding: '10px 14px',
    borderRadius: '10px',
    flex: '1',
  },
  statIcon: {
    color: '#6ee7b7',
    flexShrink: '0',
  },
  statValue: {
    fontSize: '17px',
    fontWeight: '700',
    lineHeight: '1.1',
  },
  statLabel: {
    fontSize: '9px',
    opacity: '0.6',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  
  // CAPACITÉS
  capabilities: {
    display: 'flex',
    flexDirection: 'column',
    gap: '9px',
    marginBottom: '22px',
  },
  capability: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '12.5px',
    opacity: '0.8',
  },
  capIcon: {
    opacity: '0.6',
    flexShrink: '0',
  },
  
  // AI FEATURES
  aiSection: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderRadius: '12px',
    padding: '14px 18px',
  },
  aiLabel: {
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    opacity: '0.5',
    marginBottom: '12px',
    display: 'block',
  },
  aiFeatures: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  aiFeature: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  aiFeatureIcon: {
    color: '#6ee7b7',
    flexShrink: '0',
    marginTop: '1px',
  },
  aiFeatureTitle: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#d1fae5',
    marginBottom: '1px',
  },
  aiFeatureDesc: {
    fontSize: '10.5px',
    opacity: '0.55',
  },
  
  // PANNEAU DROIT
  right: {
    width: '440px',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '40px 45px',
    backgroundColor: '#ffffff',
  },
  card: {
    width: '100%',
    maxWidth: '370px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: '10px',
  },
  avatar: {
    fontSize: '46px',
    marginBottom: '14px',
    width: '78px',
    height: '78px',
    borderRadius: '50%',
    backgroundColor: '#ecfdf5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardTitle: {
    fontSize: '22px',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '4px',
  },
  cardSubtitle: {
    fontSize: '13px',
    color: '#9ca3af',
    marginBottom: '26px',
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  input: {
    width: '100%',
    padding: '12px 15px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '13px',
    outline: 'none',
    backgroundColor: '#f9fafb',
    boxSizing: 'border-box',
  },
  passwordWrapper: {
    position: 'relative',
    width: '100%',
  },
  inputPassword: {
    width: '100%',
    padding: '12px 42px 12px 15px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '13px',
    outline: 'none',
    backgroundColor: '#f9fafb',
    boxSizing: 'border-box',
  },
  eyeButton: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: '4px',
  },
  button: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#10b981',
    color: 'white',
    fontWeight: '600',
    fontSize: '14px',
    cursor: 'pointer',
    marginTop: '4px',
    transition: 'background-color 0.2s',
  },
  buttonText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  buttonDisabled: {
    opacity: '0.6',
    cursor: 'not-allowed',
  },
  error: {
    color: '#ef4444',
    fontSize: '12px',
    textAlign: 'center',
    padding: '8px',
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    width: '100%',
  },
  forgotPassword: {
    marginTop: '14px',
    textAlign: 'center',
  },
  forgotButton: {
    background: 'none',
    border: 'none',
    color: '#10b981',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: '500',
  },
};